import { createClient } from '@supabase/supabase-js'
import { isStripeBillingEnabled } from './config/stripe'

// Supabase configuration (CRA only exposes REACT_APP_* to the browser bundle)
const supabaseUrl = String(process.env.REACT_APP_SUPABASE_URL || '').trim()
const supabaseAnonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim()

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  const missing = [
    !supabaseUrl && 'REACT_APP_SUPABASE_URL',
    !supabaseAnonKey && 'REACT_APP_SUPABASE_ANON_KEY',
  ].filter(Boolean)
  throw new Error(
    `Missing Supabase environment variable(s): ${missing.join(', ')}.\n\n` +
      'Create or edit .env.local in the project root (next to package.json), add:\n' +
      '  REACT_APP_SUPABASE_URL=https://<project-ref>.supabase.co\n' +
      '  REACT_APP_SUPABASE_ANON_KEY=<anon public key>\n\n' +
      'Use the exact names above (REACT_APP_ prefix is required). Copy values from ' +
      'Supabase Dashboard → Project Settings → API. Then stop and run `npm start` again.\n\n' +
      'If .env.local already has these but the app still fails: Create React App loads ' +
      '.env.development.local BEFORE .env.local and never overwrites an existing key. ' +
      'Remove blank REACT_APP_SUPABASE_* lines from .env.development.local, or unset them ' +
      'in your shell (`env | grep REACT_APP_SUPABASE`). Run `npm run env:check` to see ' +
      'which file wins.'
  )
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database helper functions
export const database = {
  // User operations
  async createUser(firebaseUser, stripeCustomerId = null) {
    const { data, error } = await supabase
      .from('users')
      .insert([
        {
          firebase_uid: firebaseUser.uid,
          email: firebaseUser.email,
          display_name: firebaseUser.displayName,
          photo_url: firebaseUser.photoURL,
          stripe_customer_id: stripeCustomerId,
          onboarding_completed: true // Onboarding flow disabled — send users straight to main
        }
      ])
      .select()
    
    if (error) {
      console.error('Error creating user:', error)
      throw error
    }
    
    return data[0]
  },

  async getUserByFirebaseUid(firebaseUid) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('firebase_uid', firebaseUid)
      .single()
    
    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      console.error('Error fetching user:', error)
      throw error
    }
    
    return data
  },

  // Stripe operations
  async createStripeCustomer(firebaseUser) {
    if (!isStripeBillingEnabled()) {
      return null
    }
    try {
      const response = await fetch('/.netlify/functions/create-stripe-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firebase_uid: firebaseUser.uid,
          email: firebaseUser.email,
          display_name: firebaseUser.displayName
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.skipped || !result.customer_id) {
        return null
      }
      return result.customer_id;
    } catch (error) {
      console.error('Error creating Stripe customer:', error);
      throw error;
    }
  },

  async updateUserStripeCustomerId(userId, stripeCustomerId) {
    const { data, error } = await supabase
      .from('users')
      .update({ stripe_customer_id: stripeCustomerId })
      .eq('id', userId)
      .select()
    
    if (error) {
      console.error('Error updating user Stripe customer ID:', error)
      throw error
    }
    
    return data[0]
  },

  /**
   * If billing is on and the Supabase user has no Stripe customer yet, create one via Netlify and save cus_ id.
   */
  async ensureStripeCustomerLinked(firebaseUser) {
    if (!isStripeBillingEnabled() || !firebaseUser?.uid) {
      return null
    }
    try {
      const row = await this.getUserByFirebaseUid(firebaseUser.uid)
      if (!row) return null
      if (row.stripe_customer_id) return row.stripe_customer_id
      const customerId = await this.createStripeCustomer(firebaseUser)
      if (!customerId) return null
      await this.updateUserStripeCustomerId(row.id, customerId)
      return customerId
    } catch (e) {
      console.warn('ensureStripeCustomerLinked:', e)
      return null
    }
  },

  // Quiz operations
  async saveQuiz(userId, quizData) {
    const { data, error } = await supabase
      .from('quizzes')
      .insert([
        {
          user_id: userId,
          document_name: quizData.document_name,
          quiz_data: quizData.quiz,
          question_count: quizData.question_count
        }
      ])
      .select()
    
    if (error) {
      console.error('Error saving quiz:', error)
      throw error
    }
    
    return data[0]
  },

  async getUserQuizzes(userId) {
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching quizzes:', error)
      throw error
    }
    
    return data
  },

  async getQuizById(quizId) {
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .eq('id', quizId)
      .single()
    
    if (error) {
      console.error('Error fetching quiz by ID:', error)
      throw error
    }
    
    return data
  },

  async getQuizByFirebaseUid(firebaseUid, quizId) {
    // First get the Supabase user ID from Firebase UID
    const user = await this.getUserByFirebaseUid(firebaseUid)
    if (!user) {
      throw new Error('User not found')
    }
    
    // Then get the quiz by ID and user ID
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .eq('id', quizId)
      .eq('user_id', user.id)
      .single()
    
    if (error) {
      console.error('Error fetching quiz by Firebase UID and quiz ID:', error)
      throw error
    }
    
    return data
  },

  // Flashcard operations
  async saveFlashcards(userId, flashcardData) {
    const { data, error } = await supabase
      .from('flashcards')
      .insert([
        {
          user_id: userId,
          document_name: flashcardData.document_name,
          flashcard_data: flashcardData.flashcards
        }
      ])
      .select()
    
    if (error) {
      console.error('Error saving flashcards:', error)
      throw error
    }
    
    return data[0]
  },

  async getUserFlashcards(userId) {
    const { data, error } = await supabase
      .from('flashcards')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching flashcards:', error)
      throw error
    }
    
    return data
  },

  // Document operations
  async getUserDocuments(userId) {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('user_id', userId)
      .eq('deleted', false) // Only show non-deleted documents
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching documents:', error)
      throw error
    }
    
    return data
  },

  async deleteDocument(documentId) {
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId)
    
    if (error) {
      console.error('Error deleting document:', error)
      throw error
    }
    
    return true
  },

  async softDeleteDocument(documentId) {
    const { error } = await supabase
      .from('documents')
      .update({ 
        deleted: true, 
        deleted_at: new Date().toISOString() 
      })
      .eq('id', documentId)
    
    if (error) {
      console.error('Error soft deleting document:', error)
      throw error
    }
    
    return true
  },

  // Subscription operations
  async createSubscription(userId, subscriptionData) {
    const { data, error } = await supabase
      .from('subscriptions')
      .insert([
        {
          user_id: userId,
          subscription_type: subscriptionData.type,
          stripe_subscription_id: subscriptionData.stripeId,
          status: subscriptionData.status,
          current_period_end: subscriptionData.currentPeriodEnd
        }
      ])
      .select()
    
    if (error) {
      console.error('Error creating subscription:', error)
      throw error
    }
    
    return data[0]
  },

  async getUserSubscription(userId) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .single()
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching subscription:', error)
      throw error
    }
    
    return data
  },

  // Get specific flashcard by ID
  async getFlashcardById(flashcardId) {
    const { data, error } = await supabase
      .from('flashcards')
      .select('*')
      .eq('id', flashcardId)
      .single()
    
    if (error) {
      console.error('Error fetching flashcard:', error)
      throw error
    }
    
    return data
  },

  // Usage tracking
  async trackUsage(userId, usageData) {
    const today = new Date().toISOString().split('T')[0]
    
    const { data, error } = await supabase
      .from('usage_tracking')
      .upsert([
        {
          user_id: userId,
          date: today,
          ...usageData
        }
      ])
      .select()
    
    if (error) {
      console.error('Error tracking usage:', error)
      throw error
    }
    
    return data[0]
  },

  // Quiz attempts
  async saveQuizAttempt(userId, attemptData) {
    const { data, error } = await supabase
      .from('quiz_attempts')
      .insert([{
        user_id: userId,
        quiz_id: attemptData.quizId,
        document_name: attemptData.documentName,
        score: attemptData.score,
        total_questions: attemptData.totalQuestions,
        time_spent_seconds: attemptData.timeSpentSeconds || 0,
        attempt_number: attemptData.attemptNumber || 1,
        question_answers: attemptData.questionAnswers || []
      }])
      .select()
      .single()

    if (error) {
      console.error('Error saving quiz attempt:', error)
      throw error
    }
    return data
  },

  async getQuizAttempts(userId, documentName = null) {
    let query = supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId)
      .order('completed_at', { ascending: false })

    if (documentName) {
      query = query.eq('document_name', documentName)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching quiz attempts:', error)
      throw error
    }
    return data || []
  },

  // Flashcard progress
  async saveFlashcardProgress(userId, progressData) {
    const { data, error } = await supabase
      .from('flashcard_progress')
      .upsert([{
        user_id: userId,
        flashcard_id: progressData.flashcardId,
        card_index: progressData.cardIndex,
        status: progressData.status, // 'memorized', 'still_learning', 'not_studied'
        study_count: progressData.studyCount || 0,
        correct_count: progressData.correctCount || 0,
        mastery_level: progressData.masteryLevel || 0,
        last_studied_at: new Date().toISOString()
      }], {
        onConflict: 'user_id,flashcard_id,card_index'
      })
      .select()
      .single()

    if (error) {
      console.error('Error saving flashcard progress:', error)
      throw error
    }
    return data
  },

  async getFlashcardProgress(userId, flashcardId = null) {
    let query = supabase
      .from('flashcard_progress')
      .select('*')
      .eq('user_id', userId)

    if (flashcardId) {
      query = query.eq('flashcard_id', flashcardId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching flashcard progress:', error)
      throw error
    }
    return data || []
  },

  // Learning analytics (scaffold for later phases)
  async updateLearningAnalytics(userId, documentName, analyticsData) {
    const { data, error } = await supabase
      .from('learning_analytics')
      .upsert([{
        user_id: userId,
        document_name: documentName,
        subject_area: analyticsData.subjectArea,
        gpa_score: analyticsData.gpaScore,
        total_quiz_attempts: analyticsData.totalQuizAttempts,
        average_quiz_score: analyticsData.averageQuizScore,
        total_flashcard_studied: analyticsData.totalFlashcardStudied,
        flashcard_mastery_percentage: analyticsData.flashcardMasteryPercentage,
        weak_topics: analyticsData.weakTopics || [],
        strong_topics: analyticsData.strongTopics || [],
        study_time_minutes: analyticsData.studyTimeMinutes || 0,
        last_updated: new Date().toISOString()
      }], {
        onConflict: 'user_id,document_name'
      })
      .select()
      .single()

    if (error) {
      console.error('Error updating learning analytics:', error)
      throw error
    }
    return data
  },

  async getLearningAnalytics(userId, documentName = null) {
    let query = supabase
      .from('learning_analytics')
      .select('*')
      .eq('user_id', userId)

    if (documentName) {
      query = query.eq('document_name', documentName)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching learning analytics:', error)
      throw error
    }
    return data || []
  },

  // Onboarding operations
  async saveOnboardingAnswers(userId, answers) {
    const { data, error } = await supabase
      .from('onboarding_responses')
      .upsert([{
        user_id: userId,
        user_role: answers[0], // Step 1: current role
        discovery_source: answers[1], // Step 2: how they found the app
        study_interests: answers[2], // Step 3: what they want to study
        expertise_level: answers[3] // Step 4: expertise level
      }], {
        onConflict: 'user_id'
      })
      .select()
      .single()

    if (error) {
      console.error('Error saving onboarding answers:', error)
      throw error
    }
    return data
  },

  async getOnboardingAnswers(userId) {
    const { data, error } = await supabase
      .from('onboarding_responses')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching onboarding answers:', error)
      throw error
    }
    return data
  },

  async markOnboardingCompleted(userId) {
    console.log('🔧 markOnboardingCompleted called with userId:', userId);
    console.log('🔧 UserId type:', typeof userId);
    
    const { data, error } = await supabase
      .from('users')
      .update({ onboarding_completed: true })
      .eq('id', userId)
      .select()

    if (error) {
      console.error('❌ Error marking onboarding as completed:', error)
      console.error('❌ Error details:', error.message, error.details, error.hint)
      throw error
    }
    
    console.log('✅ markOnboardingCompleted success, updated user:', data);
    console.log('✅ Number of rows updated:', data?.length);
    return data[0]
  },

  async hasCompletedOnboarding(firebaseUid) {
    try {
      const user = await this.getUserByFirebaseUid(firebaseUid);
      return user ? user.onboarding_completed : false;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return false; // Default to false if there's an error
    }
  }
}

export default supabase
